import {
  AGENDA_LABEL,
  OPS_PRM,
  buildAgenda,
  groupAgenda,
  eurK,
  monthShort,
  type AgendaEvent,
  type AgendaKind,
  type OpsChantier,
} from '@cockpit/shared';
import { cn } from '@cockpit/ui';
import { useMemo, useState } from 'react';

const KIND_TONE: Record<AgendaKind, string> = {
  start: 'bg-azur-50 text-azur-700',
  end: 'bg-navy-50 text-navy-600',
  late: 'bg-danger-50 text-danger-700',
};
const FILTERS: { k: 'all' | AgendaKind; label: string }[] = [
  { k: 'all', label: 'Tout' },
  { k: 'start', label: 'Démarrages' },
  { k: 'end', label: 'Fins prévues' },
  { k: 'late', label: 'Fins dépassées' },
];
const SHOWN = { late: 6, week: 5 } as const;

/** Agenda des prochains jours : démarrages, fins prévues et retards, par semaine. */
export function Agenda({
  rows,
  asOf,
  onOpen,
}: {
  rows: OpsChantier[];
  asOf: string;
  onOpen: (code: string) => void;
}) {
  const [filter, setFilter] = useState<'all' | AgendaKind>('all');
  const [open, setOpen] = useState<Set<string>>(new Set());
  const events = useMemo(() => buildAgenda(rows, asOf), [rows, asOf]);
  const byCode = useMemo(() => new Map(rows.map((r) => [r.code, r])), [rows]);
  const count = (k: AgendaKind) => events.filter((e) => e.k === k).length;
  const groups = groupAgenda(
    events.filter((e) => filter === 'all' || e.k === filter),
    asOf,
  );

  return (
    <section aria-labelledby="agenda" className="flex flex-col gap-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 id="agenda" className="font-display text-[19px] font-bold text-ink">
          Agenda des {OPS_PRM.hz} prochains jours
        </h2>
        <div role="group" aria-label="Filtrer l’agenda" className="flex flex-wrap gap-1.5">
          {FILTERS.map(({ k, label }) => (
            <button
              key={k}
              type="button"
              aria-pressed={filter === k}
              onClick={() => setFilter(k)}
              className={cn(
                'rounded-pill border px-3 py-1 text-[13px] transition-colors',
                filter === k
                  ? 'border-navy-600 bg-navy-600 text-on-accent'
                  : 'border-line bg-surface text-ink-2 hover:bg-surface-2',
              )}
            >
              {label} <span className="tnum opacity-80">{k === 'all' ? events.length : count(k)}</span>
            </button>
          ))}
        </div>
      </div>
      {groups.length ? (
        <div className="grid gap-4 lg:grid-cols-2">
          {groups.map((g) => {
            const max = SHOWN[g.kind];
            const all = open.has(g.label);
            return (
              <div
                key={g.label}
                className={cn(
                  'flex flex-col gap-1 rounded-md border bg-surface p-3',
                  g.kind === 'late' ? 'border-danger-100' : 'border-line-soft',
                )}
              >
                <p className="px-1 text-[11.5px] font-semibold uppercase tracking-[0.08em] text-ink-3">
                  {g.label} <span className="tnum">{g.events.length}</span>
                </p>
                {(all ? g.events : g.events.slice(0, max)).map((e) => (
                  <EventRow key={`${e.code}-${e.k}`} e={e} r={byCode.get(e.code)} onOpen={onOpen} />
                ))}
                {g.events.length > max && (
                  <button
                    type="button"
                    className="self-start px-1 text-[13px] font-semibold text-azur-700 hover:underline"
                    onClick={() =>
                      setOpen((s) => {
                        const n = new Set(s);
                        if (!n.delete(g.label)) n.add(g.label);
                        return n;
                      })
                    }
                  >
                    {all ? 'Réduire' : `Voir les ${g.events.length}`}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <p className="rounded-md border border-dashed border-line px-4 py-6 text-center text-[13.5px] text-ink-3">
          Rien de prévu dans les {OPS_PRM.hz} prochains jours.
        </p>
      )}
    </section>
  );
}

function EventRow({
  e,
  r,
  onOpen,
}: {
  e: AgendaEvent;
  r: OpsChantier | undefined;
  onOpen: (code: string) => void;
}) {
  if (!r) return null;
  const who = r.cond ?? (r.com ? `${r.com} (commercial)` : 'aucun responsable');
  return (
    <button
      type="button"
      onClick={() => onOpen(e.code)}
      className="grid grid-cols-[3.25rem_minmax(0,1fr)_auto] items-start gap-3 rounded-sm px-1 py-2 text-left hover:bg-surface-2"
    >
      <span className="flex flex-col items-center leading-none">
        <b className="tnum font-display text-[20px] text-ink">{e.d.slice(8, 10)}</b>
        <small className="text-[11px] text-ink-3">{monthShort(e.d.slice(0, 7))}</small>
      </span>
      <span className="flex min-w-0 flex-col gap-0.5">
        <span
          className={cn(
            'self-start rounded-sm px-1.5 text-[10.5px] font-bold uppercase tracking-[0.06em]',
            KIND_TONE[e.k],
          )}
        >
          {AGENDA_LABEL[e.k]}
        </span>
        <b className="truncate text-[14px] text-ink">{r.nom || r.cli || r.code}</b>
        <small className="truncate text-[12.5px] text-ink-3">
          {r.cli ? `${r.cli} · ` : ''}
          {who} · {r.ent}
        </small>
        {e.w.length > 0 && (
          <span className="flex flex-wrap gap-1">
            {e.w.map((t) => (
              <em key={t} className="rounded-sm bg-warn-50 px-1.5 text-[12px] not-italic text-warn-700">
                {t}
              </em>
            ))}
          </span>
        )}
      </span>
      <span className="flex flex-col items-end">
        <b className="tnum text-[14px] text-ink">{eurK(e.v)}</b>
        <small className="text-[11.5px] text-ink-3">{e.vl}</small>
      </span>
    </button>
  );
}
